import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import apiClient from '../../http-common';
import {
  triggerSuccess,
  triggerError,
  setErrorMessage,
  setSuccessMessage,
} from '../Request/StatusFormatter';

const AdminEditUserProfile = ({ user, userId, onUpdated }) => {
  const initialForm = {
    name: '',
    email: '',
    phone: '',
    account_number: '',
    country: '',
    transfer_pin: '',
    password: '',
    password_confirmation: '',
  };

  const [profileForm, setProfileForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState(null);

  const { mutate: loadCountry } = useMutation(
    async () => apiClient.get('/api/get-countries'),
    {
      onSuccess: (res) => setCountries(res.data.countries),
    }
  );

  const { isLoading: isSaving, mutate: saveProfile } = useMutation(
    async () => {
      const payload = {
        name: profileForm.name,
        email: profileForm.email,
        phone: profileForm.phone,
        account_number: profileForm.account_number,
        country: profileForm.country,
        transfer_pin: profileForm.transfer_pin,
      };

      if (profileForm.password) {
        payload.password = profileForm.password;
        payload.password_confirmation = profileForm.password_confirmation;
      }

      return await apiClient.post(`/api/update-user/${userId}`, payload);
    },
    {
      onSuccess: (res) => {
        setSuccessMessage(res.data);
        triggerSuccess();
        setErrors({});
        setProfileForm({
          ...profileForm,
          password: '',
          password_confirmation: '',
        });
        if (onUpdated) {
          onUpdated(res.data.user);
        }
      },
      onError: (err) => {
        const myerror = err.response?.data || err;
        setErrorMessage(myerror);
        triggerError();
        if (myerror.errors) {
          setErrors(myerror.errors);
        }
      },
    }
  );

  useEffect(() => {
    if (user && user.id) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        account_number: user.account_number || '',
        country: user.country || '',
        transfer_pin: user.transfer_pin || '',
        password: '',
        password_confirmation: '',
      });
    }
  }, [user]);

  useEffect(() => {
    loadCountry();
  }, []);

  const onChange = (e) =>
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });

  const fieldError = (field) => {
    if (!errors || !errors[field]) return null;
    const value = errors[field];
    return Array.isArray(value) ? value[0] : value;
  };

  if (!user || !user.id) {
    return null;
  }

  return (
    <div className="card card-style px-0">
      <div className="content">
        <div className="d-flex justify-content-center p-3 pb-0">
          <span className="icon icon-xxl gradient-highlight me-2 shadow-bg shadow-bg-s rounded-s">
            <img src={user.profile} width="45" className="rounded-s" alt="profile" />
          </span>
        </div>
        <h4 className="text-center color-theme mb-0">Edit User Profile</h4>
        <p className="text-center font-12 opacity-70">{user.email}</p>

        <span className="text-danger">{fieldError('name')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>person</i>
          <input value={profileForm.name} onChange={onChange} type="text" name="name" className="form-control bg-theme color-theme rounded-xs" placeholder="Full name" />
          <label className="form-label-always-active color-highlight font-11">Full Name</label>
        </div>

        <span className="text-danger">{fieldError('email')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>email</i>
          <input value={profileForm.email} onChange={onChange} type="email" name="email" className="form-control bg-theme color-theme rounded-xs" placeholder="Email" />
          <label className="form-label-always-active color-highlight font-11">Email</label>
        </div>

        <span className="text-danger">{fieldError('phone')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>phone</i>
          <input value={profileForm.phone} onChange={onChange} type="tel" name="phone" className="form-control bg-theme color-theme rounded-xs" placeholder="Phone" />
          <label className="form-label-always-active color-highlight font-11">Phone</label>
        </div>

        <span className="text-danger">{fieldError('country')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>public</i>
          <select value={profileForm.country} onChange={onChange} name="country" className="form-control bg-theme color-theme rounded-xs">
            <option value="">Select country</option>
            {countries !== null &&
              countries.map((country) => (
                <option key={country.name} value={country.name}>{country.name}</option>
              ))}
          </select>
          <label className="form-label-always-active color-highlight font-11">Country</label>
        </div>

        <span className="text-danger">{fieldError('account_number')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>badge</i>
          <input value={profileForm.account_number} onChange={onChange} type="text" name="account_number" className="form-control bg-theme color-theme rounded-xs" placeholder="Account number" />
          <label className="form-label-always-active color-highlight font-11">Account Number</label>
        </div>

        <span className="text-danger">{fieldError('transfer_pin')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>pin</i>
          <input value={profileForm.transfer_pin} onChange={onChange} type="text" name="transfer_pin" className="form-control bg-theme color-theme rounded-xs" placeholder="Transfer pin" />
          <label className="form-label-always-active color-highlight font-11">Transfer Pin</label>
        </div>

        <p className="font-12 opacity-70 mb-2">
          Current password: <strong>{user.enc_copy || '—'}</strong>
        </p>

        <span className="text-danger">{fieldError('password')}</span>
        <div className="form-custom form-label form-icon mb-2">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>lock</i>
          <input value={profileForm.password} onChange={onChange} type="password" name="password" className="form-control bg-theme color-theme rounded-xs" placeholder="New password" autoComplete="new-password" />
          <label className="form-label-always-active color-highlight font-11">New Password</label>
        </div>

        <span className="text-danger">{fieldError('password_confirmation')}</span>
        <div className="form-custom form-label form-icon mb-3">
          <i className="material-icons-outlined" style={{ fontSize: '18px' }}>lock</i>
          <input value={profileForm.password_confirmation} onChange={onChange} type="password" name="password_confirmation" className="form-control bg-theme color-theme rounded-xs" placeholder="Confirm password" autoComplete="new-password" />
          <label className="form-label-always-active color-highlight font-11">Confirm Password</label>
        </div>

        <div onClick={saveProfile} className="btn btn-full gradient-highlight shadow-bg shadow-bg-s">
          {isSaving ? <span>saving...</span> : <span>Save user profile</span>}
        </div>
      </div>
    </div>
  );
};

export default AdminEditUserProfile;
